'use client';

import { useMemo, useEffect, useState } from 'react';

import {
  getRouteByKey,
  getRouteByImplementedPath,
  getRouteByMarketingUrl,
  resolveMarketingUrl,
} from '@/constants/routes.mapping';
import { megaMenuData as rawMenuData } from '@/data/megaMenuData';
import { cleanSpeciality } from '@/lib/specialities/deriveSpeciality';

import type {
  ConsultantCard,
  DropdownColumn,
  DropdownItem,
  DynamicSource,
  NavItemData,
} from '@/types/header.types';

export interface ServiceEntry {
  title: string;
  slug: string;
  apiId?: string;
}

const PHARMACY_SERVICES: DropdownItem[] = [
  { label: 'Everyday Services', href: '/pharmacy/everyday-services/' },
  { label: 'Health Consultations', href: '/pharmacy/consultations/' },
];

const TEST_SCAN_SLUGS = [
  'radiology',
  'blood-tests',
  'bone-scan',
  'ultrasound',
  'liver-health-fibroscan',
  'audiology-hearing-test-battersea-london',
];

const TESTS_SCANS_LABELS: Record<string, string> = {
  'liver-health-fibroscan': 'Liver Health (FibroScan)',
  'audiology-hearing-test-battersea-london': 'Audiology Test',
};

function toTitle(slug: string): string {
  return slug
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function resolveServiceUrl(slug: string): string {
  const route =
    getRouteByKey(`speciality-${slug}`) ??
    getRouteByMarketingUrl(`/specialities/${slug}`) ??
    getRouteByImplementedPath(`/${slug}`);
  return route?.marketingUrl ?? '';
}

function resolveServiceItems(
  allServices: ServiceEntry[],
  filter: string[] | undefined,
  limit: number | undefined,
  labelMap?: Record<string, string>,
  /** When true, each item gets a `specialitySlug` field for hover matching. */
  tagWithSlug?: boolean,
): DropdownItem[] {
  const slugs = filter ?? allServices.map((s) => s.slug);
  const items: DropdownItem[] = slugs
    .map((slug) => {
      const href = resolveServiceUrl(slug);
      if (!href) return null;

      const svc = allServices.find((s) => s.slug === slug);
      const label = labelMap?.[slug] ?? svc?.title ?? toTitle(slug);
      return {
        label,
        href,
        ...(tagWithSlug ? { specialitySlug: slug } : {}),
      };
    })
    .filter((item): item is DropdownItem => item !== null);
  return limit ? items.slice(0, limit) : items;
}

function resolveSpecialityItems(
  allServices: ServiceEntry[],
  filter: string[] | undefined,
  limit: number | undefined,
): DropdownItem[] {
  return resolveServiceItems(allServices, filter, limit, undefined, true);
}

function resolveConsultants(
  doctors: ConsultantCard[],
  filter: string[] | undefined,
  limit: number | undefined,
  randomize?: boolean,
): ConsultantCard[] {
  let pinned: ConsultantCard[] = [];
  let rest: ConsultantCard[] = [];

  if (filter && filter.length > 0) {
    const pinnedSlugs = new Set(filter);
    pinned = filter
      .map((slug) => doctors.find((d) => d.id === slug))
      .filter(Boolean) as ConsultantCard[];
    rest = doctors.filter((d) => !pinnedSlugs.has(d.id));
  } else {
    rest = doctors;
  }

  if (randomize) {
    rest = shuffleArray(rest);
  }

  const combined = [...pinned, ...rest];
  return limit ? combined.slice(0, limit) : combined;
}

function resolvePharmacyServices(
  filter: string[] | undefined,
  limit: number | undefined,
): DropdownItem[] {
  let items = PHARMACY_SERVICES;
  if (filter && filter.length > 0) {
    items = filter
      .map((slug) => PHARMACY_SERVICES.find((p) => p.href.includes(slug)))
      .filter(Boolean) as DropdownItem[];
  }
  return limit ? items.slice(0, limit) : items;
}

export interface DoctorLike {
  slug: string;
  fullName: string;
  heading?: string | null;
  primarySpeciality?: string | null;
  specialities?: string[] | null;
  categoryId?: string | number | null;
  apiId?: string | number | null;
  image?: { url?: string } | null;
}

export function slugifySpeciality(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function getServiceSlugsForSpeciality(specialityText: string): string[] {
  const normalized = specialityText.toLowerCase().trim();
  const baseSlug = slugifySpeciality(specialityText);
  const cleaned = cleanSpeciality(specialityText);
  const slugs = new Set<string>();

  if (baseSlug) slugs.add(baseSlug);
  if (cleaned?.slug) slugs.add(cleaned.slug);

  // Map known aliases between doctor data speciality titles and page route slugs
  if (normalized.includes('paediatric') || normalized.includes('children')) {
    slugs.add('childrens-health-paediatrics');
    slugs.add('private-paediatrician');
  }
  if (
    normalized.includes('gynaecolog') ||
    normalized.includes('obstetric') ||
    normalized.includes("women's health")
  ) {
    slugs.add('gynaecology-and-obstetrics');
  }
  if (
    normalized.includes('gp') ||
    normalized.includes('general practice') ||
    normalized.includes('general practitioner')
  ) {
    slugs.add('gp');
    slugs.add('general-practice');
    slugs.add('private-gp');
  }
  if (normalized.includes('aesthetic')) {
    slugs.add('aesthetic');
    slugs.add('aesthetics-clinic');
  }
  if (
    normalized.includes('gastroenterology') ||
    normalized.includes('hepatology')
  ) {
    slugs.add('gastroenterology');
  }
  if (
    normalized.includes('psychiatry') ||
    normalized.includes('psychotherapy') ||
    normalized.includes('psychotherapeutic')
  ) {
    slugs.add('psychiatry');
  }
  if (
    normalized.includes('respiratory') ||
    normalized.includes('sleep apnoea')
  ) {
    slugs.add('obstructive-sleep-apnoea');
    slugs.add('sleep-apnoea');
  }
  if (
    normalized.includes('neurosurgery') ||
    normalized.includes('spinal') ||
    normalized.includes('spine')
  ) {
    slugs.add('neurosurgery');
    slugs.add('spine-and-neurosurgery');
  }
  if (normalized.includes('audiology') || normalized.includes('hearing')) {
    slugs.add('audiology-hearing-test-battersea-london');
  }
  if (normalized.includes('radiology') || normalized.includes('imaging')) {
    slugs.add('radiology');
  }
  if (normalized.includes('nutrition') || normalized.includes('diet')) {
    slugs.add('dietetics');
  }
  if (normalized.includes('breast')) {
    slugs.add('breast-clinic');
  }
  if (
    normalized.includes('orthopaedic') ||
    normalized.includes('trauma') ||
    normalized.includes('knee') ||
    normalized.includes('hip') ||
    normalized.includes('joint')
  ) {
    slugs.add('trauma-and-orthopaedics');
    slugs.add('trauma-and-orthopaedics-upper-limb');
    slugs.add('hip-and-knee-replacement');
    slugs.add('knee-osteoarthritis');
  }
  if (normalized.includes('sports') || normalized.includes('physio')) {
    slugs.add('physiotherapy');
    slugs.add('sports-and-exercise-medicine');
  }
  if (
    baseSlug.split('-').includes('urology') ||
    normalized.includes('andrology') ||
    normalized.includes('prostate')
  ) {
    slugs.add('urology-and-andrology');
  }
  if (normalized.includes('psychosexual')) {
    slugs.add('sexual-health');
  }
  if (normalized.includes('menopause')) {
    slugs.add('menopause-clinic');
  }
  if (
    normalized.includes('post-surgery') ||
    normalized.includes('loveday') ||
    normalized.includes('recovery')
  ) {
    slugs.add('post-surgery-recovery');
    slugs.add('loveday');
    slugs.add('post-surgery-recovery-loveday');
  }
  if (
    normalized.includes('fertility') ||
    normalized.includes('subfertility') ||
    normalized.includes('ivf')
  ) {
    slugs.add('fertility');
    slugs.add('female-fertility');
  }

  return Array.from(slugs);
}

export function mapDoctorToConsultant(d: DoctorLike): ConsultantCard {
  return {
    id: d.slug,
    slug: d.slug,
    name: d.fullName,
    qualifications: d.heading ?? '',
    speciality: d.primarySpeciality ?? '',
    imageUrl: d.image?.url ?? undefined,
    href: `/our-specialists/${d.slug}/`,
  };
}

/**
 * Builds a map of speciality slug → ConsultantCard[] from raw doctors and services list.
 * First matches by categoryId / apiId where available, then falls back to title/slug text matching.
 */
export function buildSpecialistsBySpeciality(
  doctors: DoctorLike[],
  services: ServiceEntry[] = [],
): Map<string, ConsultantCard[]> {
  const map = new Map<string, ConsultantCard[]>();

  // Map service categoryId (apiId) -> ServiceEntry.slug
  const serviceByCategoryId = new Map<string, string>();
  for (const svc of services) {
    if (svc.apiId) {
      serviceByCategoryId.set(String(svc.apiId), svc.slug);
    }
  }

  for (const doc of doctors) {
    const card = mapDoctorToConsultant(doc);
    const slugs = new Set<string>();

    // 1. Primary matching via categoryId / apiId
    if (doc.categoryId !== null && doc.categoryId !== undefined) {
      const categorySlug = serviceByCategoryId.get(String(doc.categoryId));
      if (categorySlug) {
        slugs.add(categorySlug);
      }
    }

    // 2. Secondary matching via text / aliases if categoryId wasn't found or for extra coverage
    if (doc.primarySpeciality) {
      const resolved = getServiceSlugsForSpeciality(doc.primarySpeciality);
      for (const s of resolved) {
        slugs.add(s);
      }
    }
    if (doc.specialities) {
      for (const s of doc.specialities) {
        if (s) {
          const resolved = getServiceSlugsForSpeciality(s);
          for (const rs of resolved) {
            slugs.add(rs);
          }
        }
      }
    }

    for (const slug of slugs) {
      const list = map.get(slug);
      if (list) {
        // Prevent duplicate doctors in the same speciality list
        if (!list.some((c) => c.slug === card.slug)) {
          list.push(card);
        }
      } else {
        map.set(slug, [card]);
      }
    }
  }

  return map;
}

function shuffleArray<T>(array: T[]): T[] {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

function resolveColumn(
  col: DropdownColumn,
  allServices: ServiceEntry[],
  allDoctors: ConsultantCard[],
): DropdownColumn {
  const source = col.dynamicSource as DynamicSource | undefined;
  if (!source) return col;

  const filter =
    typeof col.dynamicFilter === 'function'
      ? col.dynamicFilter()
      : col.dynamicFilter;

  const allServiceSlugs = allServices.map((s) => s.slug);
  let specialitySlugs = allServiceSlugs.filter(
    (slug) => !TEST_SCAN_SLUGS.includes(slug),
  );

  if (col.randomize) {
    specialitySlugs = shuffleArray(specialitySlugs);
  }

  switch (source) {
    case 'tests-scans':
      return {
        ...col,
        items: resolveServiceItems(
          allServices,
          filter ?? TEST_SCAN_SLUGS,
          col.dynamicLimit,
          TESTS_SCANS_LABELS,
        ),
      };
    case 'specialities':
      return {
        ...col,
        items: resolveSpecialityItems(
          allServices,
          filter ?? specialitySlugs,
          col.dynamicLimit,
        ),
      };
    case 'services':
      return {
        ...col,
        items: resolveServiceItems(
          allServices,
          filter ?? specialitySlugs,
          col.dynamicLimit,
        ),
      };
    case 'specialists':
      return {
        ...col,
        type: 'consultants' as const,
        consultants: resolveConsultants(
          allDoctors,
          filter,
          col.dynamicLimit,
          col.randomize,
        ),
      };
    case 'pharmacy-services':
      return {
        ...col,
        items: resolvePharmacyServices(filter, col.dynamicLimit),
      };
    default:
      return col;
  }
}

function resolveColumnUrls(col: DropdownColumn): DropdownColumn {
  return {
    ...col,
    items: col.items?.map((item) => ({
      ...item,
      href: resolveMarketingUrl(item.href) || item.href,
    })),
    viewAll: col.viewAll
      ? {
          ...col.viewAll,
          href: resolveMarketingUrl(col.viewAll.href) || col.viewAll.href,
        }
      : undefined,
    consultants: col.consultants?.map((c) => ({
      ...c,
      href: resolveMarketingUrl(c.href) || c.href,
    })),
  };
}

function resolveNavItemUrls(
  item: NavItemData,
  allServices: ServiceEntry[],
  allDoctors: ConsultantCard[],
): NavItemData {
  return {
    ...item,
    href: resolveMarketingUrl(item.href) || item.href,
    columns: item.columns?.map((col) =>
      resolveColumnUrls(resolveColumn(col, allServices, allDoctors)),
    ),
    mobileColumns: item.mobileColumns?.map((col) =>
      resolveColumnUrls(resolveColumn(col, allServices, allDoctors)),
    ),
    featuredCard: item.featuredCard
      ? {
          ...item.featuredCard,
          href:
            resolveMarketingUrl(item.featuredCard.href) ||
            item.featuredCard.href,
        }
      : undefined,
  };
}

export interface MegaMenuResult {
  navItems: NavItemData[];
  specialistsBySpeciality: Map<string, ConsultantCard[]>;
}

const EMPTY_SPECIALISTS_MAP = new Map<string, ConsultantCard[]>();

export function useMegaMenuData(): MegaMenuResult {
  const [allServices, setAllServices] = useState<ServiceEntry[]>([]);
  const [allDoctors, setAllDoctors] = useState<ConsultantCard[]>([]);
  const [specialistsBySpeciality, setSpecialistsBySpeciality] = useState<
    Map<string, ConsultantCard[]>
  >(EMPTY_SPECIALISTS_MAP);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    Promise.all([
      import('@/data/services').then(
        (m) => (m.allServices ?? []) as ServiceEntry[],
      ),
      import('@/features/specialists/hooks/fetchData').then((m) => {
        const docs = m.getAllDoctors();
        return docs as DoctorLike[];
      }),
    ]).then(([services, rawDoctors]) => {
      setAllServices(services);
      setAllDoctors(rawDoctors.map(mapDoctorToConsultant));
      setSpecialistsBySpeciality(
        buildSpecialistsBySpeciality(rawDoctors, services),
      );
      setLoaded(true);
    });
  }, []);

  const navItems = useMemo(() => {
    const data = rawMenuData as NavItemData[];
    if (!loaded) {
      return data.map((item) => ({
        ...item,
        href: resolveMarketingUrl(item.href) || item.href,
        featuredCard: item.featuredCard
          ? {
              ...item.featuredCard,
              href:
                resolveMarketingUrl(item.featuredCard.href) ||
                item.featuredCard.href,
            }
          : undefined,
      }));
    }
    return data.map((item) =>
      resolveNavItemUrls(item, allServices, allDoctors),
    );
  }, [loaded, allServices, allDoctors]);

  return useMemo(
    () => ({ navItems, specialistsBySpeciality }),
    [navItems, specialistsBySpeciality],
  );
}
